/**
 * Personnalisation de l'accueil : la salutation, et le mot d'esprit rare.
 *
 * Deux principes gouvernent ce fichier, et ils tirent dans des sens opposés.
 *
 * Le premier : cette page est vue plusieurs fois par jour. Tout ce qui s'y
 * affiche systématiquement doit rester sobre. Une plaisanterie sur le chemin
 * principal cesse d'être drôle au troisième passage, devient du papier peint au
 * dixième, et agace au centième. La salutation est donc juste juste.
 *
 * Le second : une application interne n'est pas obligée d'être froide. Mais
 * l'esprit ne se paie que là où il ne coûte rien, c'est-à-dire dans les moments
 * rares, et jamais à la place d'une information utile.
 *
 * D'où la répartition : la salutation s'adapte à l'heure et ne plaisante pas,
 * le clin d'oeil n'apparaît que dans trois circonstances, et le reste du temps
 * il n'y a rien du tout. Le silence est la valeur par défaut.
 *
 * Toutes les fonctions prennent la date en paramètre plutôt que d'appeler
 * l'horloge : c'est ce qui les rend vérifiables.
 */

export type MomentDeLaJournee = 'nuit' | 'matin' | 'apresMidi' | 'soir';

/**
 * Les bornes suivent l'usage français, pas une division en quarts égaux.
 * « Bonjour » couvre le matin, « Bon après-midi » prend le relais à midi, et
 * « Bonsoir » commence à dix-huit heures. La nuit se distingue de la soirée
 * pour le clin d'oeil, mais se salue de la même façon : personne n'a envie
 * qu'une application lui fasse remarquer l'heure en la nommant.
 */
export function momentDeLaJournee(maintenant: Date): MomentDeLaJournee {
    const heure = maintenant.getHours();
    if (heure >= 22 || heure < 5) return 'nuit';
    if (heure < 12) return 'matin';
    if (heure < 18) return 'apresMidi';
    return 'soir';
}

const SALUTATIONS: Record<MomentDeLaJournee, string> = {
    nuit: 'Bonsoir',
    matin: 'Bonjour',
    apresMidi: 'Bon après-midi',
    soir: 'Bonsoir',
};

/** La salutation seule, sans le prénom : l'appelant décide de la ponctuation. */
export function salutation(maintenant: Date): string {
    return SALUTATIONS[momentDeLaJournee(maintenant)];
}

/**
 * Salutation complète. Le prénom peut manquer, sur un compte dont l'annuaire
 * n'a pas encore été lu : la virgule disparaît alors avec lui, plutôt que de
 * laisser « Bonjour, » en suspens.
 */
export function salutationComplete(maintenant: Date, prenom?: string | null): string {
    const propre = prenom?.trim();
    return propre ? `${salutation(maintenant)}, ${propre}` : salutation(maintenant);
}

/**
 * Rang du jour dans l'année, pour faire tourner les formulations.
 *
 * Le tirage est volontairement lié au jour et non au hasard : une phrase qui
 * change à chaque rendu clignote, et une phrase tirée au sort peut sortir deux
 * fois de suite. Là, elle tient la journée puis passe à la suivante.
 */
function jourDeLAnnee(maintenant: Date): number {
    const debutAnnee = new Date(maintenant.getFullYear(), 0, 1);
    const ecart = maintenant.getTime() - debutAnnee.getTime();
    return Math.floor(ecart / 86_400_000);
}

function auTour<T>(choix: readonly T[], maintenant: Date): T {
    return choix[jourDeLAnnee(maintenant) % choix.length];
}

const NUIT = [
    'À cette heure, il ne reste que vous et les tâches planifiées.',
    'Le catalogue tourne toujours. Lui n’a pas besoin de sommeil, juste d’un redémarrage de temps en temps.',
    'Personne ne déploie à cette heure. Personne de raisonnable, en tout cas.',
    'À une heure pareille, les bonnes idées ressemblent beaucoup aux mauvaises.',
] as const;

const VENDREDI_SOIR = [
    'Bon week-end. Et pas de mise en production le vendredi soir, c’est écrit dans tous les runbooks du monde.',
    'Les accès ne s’évaporent pas pendant le week-end, vous pouvez fermer l’onglet.',
    'Bon week-end : le catalogue sera là lundi, c’est à peu près sa seule promesse.',
] as const;

const LUNDI_MATIN = [
    'Le café d’abord, les accès ensuite.',
    'Nouvelle semaine, cache vidé, on repart.',
    'Lundi, le seul jour où « ça marchait vendredi » est une phrase recevable.',
] as const;

const RECHERCHE_VIDE = [
    'Zéro résultat, ce qui est techniquement une réponse valide.',
    'Le filtre fait son travail, peut-être un peu trop bien.',
    'Essayez avec moins de précision : c’est souvent plus efficace.',
    'Il n’y a rien ici, et pour une fois ce n’est pas un problème de cache.',
] as const;

/**
 * La phrase qui accompagne une recherche sans résultat.
 *
 * Contrairement au clin d'oeil, celle-ci s'affiche toujours : un écran vide est
 * un cul-de-sac, et il ne coûte rien d'y mettre un peu de légèreté. Elle
 * précède le conseil utile sans le remplacer, l'utilisateur ayant surtout
 * besoin de savoir quoi faire ensuite.
 */
export function motSurLeVide(maintenant: Date): string {
    return auTour(RECHERCHE_VIDE, maintenant);
}

/**
 * Le mot d'esprit, ou rien.
 *
 * Trois circonstances seulement, choisies parce qu'elles sont rares et parce
 * qu'on y a la tête à autre chose qu'à travailler : la nuit, le vendredi en fin
 * de journée, le lundi matin. Le reste de la semaine, la fonction rend `null`
 * et l'accueil reste strictement informatif.
 *
 * L'ordre des tests compte : une nuit de vendredi à samedi relève de la nuit,
 * pas de la fin de semaine de travail.
 */
export function clinDOeil(maintenant: Date): string | null {
    const moment = momentDeLaJournee(maintenant);
    const jour = maintenant.getDay();

    if (moment === 'nuit') return auTour(NUIT, maintenant);
    if (jour === 5 && (moment === 'soir' || maintenant.getHours() >= 17)) {
        return auTour(VENDREDI_SOIR, maintenant);
    }
    if (jour === 1 && moment === 'matin') return auTour(LUNDI_MATIN, maintenant);
    return null;
}

/**
 * La date du jour, en clair.
 *
 * Le premier caractère est mis en capitale : `toLocaleDateString` rend
 * « vendredi 21 août 2026 », et une date qui ouvre une ligne se capitalise.
 */
export function dateEnClair(maintenant: Date): string {
    const texte = maintenant.toLocaleDateString('fr-FR', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    });
    return texte.charAt(0).toUpperCase() + texte.slice(1);
}
